import { Fragment, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  Building2,
  ChevronDown,
  ChevronRight,
  FileStack,
  Home,
  Mail,
  Phone,
  Plus,
  Search,
  Upload,
} from 'lucide-react'

import { AppShell } from '@/admin/components/app-shell'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { BulkImportDialog } from '@/admin/components/imports/bulk-import-dialog'
import { GroupPropertyRows } from '@/admin/components/groups/group-property-rows'
import { EditFormDialog } from '@/admin/components/shared/edit-form-dialog'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { HostSearchInput } from '@/admin/components/shared/host-search-input'
import { StatCards } from '@/admin/components/shared/stat-cards'
import { TaskTile, TaskTiles } from '@/admin/components/shared/task-tile'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { useGroupSummaries, type GroupSummary } from '@/admin/hooks/use-property-groups'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { SearchInput } from '@/shared/components/ui/search-input'
import { filterBySearch } from '@/shared/lib/search-match'
import { defineField, formatPhoneDisplay } from '@/shared/lib/forms'
import type { FormFieldMeta } from '@/shared/lib/forms'

/**
 * The groups home: the three things people come here to do, where things
 * stand, then every group with its properties and open work. Decision first, data
 * second; large type and big targets throughout.
 */
export function GroupsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = usePermissions()
  // The same permissions the group endpoints check.
  const canCreate = can('groups.create')
  const canEdit = can('groups.edit.global')
  const canAddProperty = can('hosts.create')
  const { data: groupRows = [], isLoading, isError, refetch } = useGroupSummaries()

  const [search, setSearch] = useState('')
  const [findOpen, setFindOpen] = useState(false)
  // Which upload the group picker leads to: properties or claims.
  const [pickFor, setPickFor] = useState<'properties' | 'claims' | null>(null)
  const [uploadTo, setUploadTo] = useState<GroupSummary | null>(null)
  const [claimsTo, setClaimsTo] = useState<GroupSummary | null>(null)
  const canFileClaims = can('claims.create')
  const claimUploadTarget = groupRows.find((h) => h.claim_upload_target)?.claim_upload_target ?? null

  const totals = useMemo(
    () => ({
      properties: groupRows.reduce((n, h) => n + h.property_count, 0),
      registered: groupRows.reduce((n, h) => n + h.registered_products, 0),
      openClaims: groupRows.reduce((n, h) => n + h.open_claims, 0),
      openTransfers: groupRows.reduce((n, h) => n + h.open_transfers, 0),
    }),
    [groupRows],
  )

  const visible = useMemo(
    () =>
      filterBySearch(groupRows, search, (h) =>
        [h.name, h.mailing_address_display, h.contact_name, h.contact_email].join(' '),
      ),
    [groupRows, search],
  )

  const pickGroupFields = useMemo<FormFieldMeta[]>(
    () => [
      defineField({
        code_name: 'group',
        display_name:
          pickFor === 'claims'
            ? t('groups.home.whichGroupClaims', 'Which {{groupNoun}} are these claims for?')
            : t('groups.home.whichGroup', 'Which {{groupNoun}} are these properties in?'),
        field_type: 'SELECT',
        required: true,
        options: groupRows.map((h) => ({ value: h.uuid, label: h.name })),
      }),
    ],
    [t, groupRows, pickFor],
  )

  // Groups whose properties are shown, indented under the group's row.
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const withProperties = useMemo(
    () => groupRows.filter((h) => h.property_count > 0).map((h) => h.uuid),
    [groupRows],
  )
  const allExpanded =
    withProperties.length > 0 && withProperties.every((uuid) => expanded.has(uuid))
  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(withProperties))
  const toggle = (uuid: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })

  const columns: DataTableColumn<GroupSummary>[] = [
    {
      id: 'group',
      header: t('groups.home.colGroup', '{{groupNoun}}'),
      cell: (h) => {
        const open = expanded.has(h.uuid)
        return (
          <div className="flex items-start gap-2 py-1">
            {h.property_count > 0 ? (
              <Button
                variant="ghost"
                size="icon-xs"
                aria-expanded={open}
                aria-label={
                  open
                    ? t('groups.home.hideProperties', 'Hide properties')
                    : t('groups.home.showProperties', 'Show properties')
                }
                onClick={(e) => {
                  e.stopPropagation()
                  toggle(h.uuid)
                }}
                className="mt-0.5 cursor-pointer"
              >
                {open ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            ) : (
              <span className="w-6 shrink-0" />
            )}
            <div className="w-[280px] whitespace-normal">
              <p className="flex items-start gap-2 text-[15px] text-[var(--ink)]">
                <Building2 className="mt-1 h-4 w-4 shrink-0 text-[var(--green)]" />
                {h.name}
              </p>
              <p className="mt-0.5 text-[13px] text-[var(--ink-2)]">
                {h.mailing_address_display || '-'}
              </p>
            </div>
          </div>
        )
      },
    },
    {
      id: 'contact',
      header: t('groups.home.colContact', 'Contact'),
      cell: (h) => (
        <div className="w-[180px] whitespace-normal py-1 text-[13px] text-[var(--ink-2)]">
          <p className="text-[14px] text-[var(--ink)]">{h.contact_name || '-'}</p>
          {h.contact_phone ? (
            <p className="mt-0.5 flex items-center gap-1.5">
              <Phone className="h-3 w-3" />
              {formatPhoneDisplay(h.contact_phone)}
            </p>
          ) : h.contact_email ? (
            <p className="mt-0.5 flex items-center gap-1.5">
              <Mail className="h-3 w-3" />
              {h.contact_email}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      id: 'properties',
      header: t('groups.home.colProperties', 'Properties'),
      cell: (h) => <span className="text-[15px]">{h.property_count}</span>,
    },
    {
      id: 'registered',
      header: t('groups.home.colRegistered', 'Registered'),
      cell: (h) => <span className="text-[15px]">{h.registered_products}</span>,
    },
    {
      id: 'claims',
      header: t('groups.home.colOpenClaims', 'Open Claims'),
      cell: (h) =>
        h.open_claims > 0 ? (
          <Badge variant="destructive">{h.open_claims}</Badge>
        ) : (
          <span className="text-[15px] text-[var(--ink-2)]">0</span>
        ),
    },
    {
      id: 'transfers',
      header: t('groups.home.colOpenTransfers', 'Open Transfers'),
      cell: (h) => <span className="text-[15px]">{h.open_transfers}</span>,
    },
  ]

  return (
    <AppShell>
      <main className="page-container py-10">
        {/* Header: what this page is, and the one primary action. */}
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <h1 className="clara-title">
              {t('groups.home.title', '{{groupNounPlural}}')}
            </h1>
            <p className="mt-2 max-w-[620px] text-[14px] leading-[1.6] text-[var(--ink-2)]">
              {t(
                'groups.home.subtitle',
                'Every {{groupNoun}}, its properties and its open work in one place. Pick a task below, or open a {{groupNoun}} from the list.',
              )}
            </p>
          </div>
          {(canAddProperty || canCreate) && (
            <div className="flex gap-2">
              {canAddProperty && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/properties/new')}
                  className="h-10 cursor-pointer gap-1.5 rounded-[4px] px-4 text-[12px] uppercase tracking-[0.1em]"
                >
                  <Home className="h-3.5 w-3.5" />
                  {t('groups.home.addProperty', 'Add Property')}
                </Button>
              )}
              {canCreate && (
                <Button
                  onClick={() => navigate('/groups/new')}
                  className="h-10 cursor-pointer gap-1.5 rounded-[4px] border-none bg-[var(--green)] px-4 text-[12px] uppercase tracking-[0.1em] text-white hover:bg-[var(--green-hover)]"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('groups.home.addGroup', 'Add {{groupNoun}}')}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* The three jobs people come here for. */}
        <TaskTiles>
          {canCreate && (
            <TaskTile
              icon={Building2}
              title={t('groups.home.taskAddTitle', 'Set up a new {{groupNoun}}')}
              description={t(
                'groups.home.taskAddBody',
                'Name, mailing address and who to contact. Takes a minute.',
              )}
              onClick={() => navigate('/groups/new')}
              testId="group-task-add"
            />
          )}
          {canEdit && (
            <TaskTile
              icon={Upload}
              title={t('groups.home.taskUploadTitle', "Upload a {{groupNoun}}'s properties")}
              description={t(
                'groups.home.taskUploadBody',
                'One spreadsheet. We find what is already registered and register the rest.',
              )}
              onClick={() => setPickFor('properties')}
              testId="group-task-upload"
            />
          )}
          {canFileClaims && claimUploadTarget && (
            <TaskTile
              icon={FileStack}
              title={t('groups.home.taskClaimsTitle', "Upload a {{groupNoun}}'s claims")}
              description={t(
                'groups.home.taskClaimsBody',
                'One spreadsheet of affected properties opens one {{groupNoun}} claim with a line per property.',
              )}
              onClick={() => setPickFor('claims')}
              testId="group-task-claims"
            />
          )}
          <TaskTile
            icon={Search}
            title={t('groups.home.taskFindTitle', 'Find a property')}
            description={t(
              'groups.home.taskFindBody',
              'Search by address, ZIP, owner or registration number.',
            )}
            onClick={() => setFindOpen(true)}
            testId="group-task-find"
          />
        </TaskTiles>

        {/* Where things stand. Each figure opens the records behind it:
            properties unfold under their groups below, the rest open in their
            own section, filtered to groups. */}
        <StatCards
          cards={[
            { key: 'groupRows', label: t('groups.home.statGroups', '{{groupNounPlural}}'), value: groupRows.length },
            {
              key: 'properties',
              label: t('groups.home.statProperties', 'Properties'),
              value: totals.properties,
              hint: allExpanded
                ? t('groups.home.statHideProperties', 'Select to fold them away')
                : t('groups.home.statShowProperties', 'Select to show them under each {{groupNoun}}'),
              onClick: toggleAll,
              active: allExpanded,
            },
            {
              key: 'registered',
              label: t('groups.home.statRegistered', 'Registered Products'),
              value: totals.registered,
              hint: t('groups.home.statInRegistrations', 'Open in Registrations'),
              onClick: () => navigate('/registrations?group=any'),
            },
            {
              key: 'claims',
              label: t('groups.home.statOpenClaims', 'Open Claims'),
              value: totals.openClaims,
              hint: t('groups.home.statInClaims', 'Open in Claims'),
              onClick: () => navigate('/claims?group=any'),
            },
            {
              key: 'transfers',
              label: t('groups.home.statOpenTransfers', 'Open Transfers'),
              value: totals.openTransfers,
              hint: t('groups.home.statInTransfers', 'Open in Transfers'),
              onClick: () => navigate('/transfers?group=any'),
            },
          ]}
        />

        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="clara-section-label">{t('groups.home.listTitle', 'Your {{groupNounPlural}}')}</h2>
          <div className="w-full md:w-[340px]">
            <SearchInput
              value={search}
              onChange={setSearch}
              iconSize={16}
              placeholder={t('groups.home.searchPlaceholder', 'Search {{groupNoun}}, address or contact')}
              aria-label={t('groups.home.searchLabel', 'Search {{groupNounPlural}}')}
              className="h-10 text-[14px]"
            />
          </div>
        </div>

        <DataTable<GroupSummary>
          tableId="groups-home"
          columns={columns}
          data={visible}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          rowKey="uuid"
          // Each group's row, then - when opened - its properties as rows of
          // the same table, as the Product Library shows a product's variants.
          renderRow={(h, cols) => (
            <Fragment key={h.uuid}>
              <tr
                onClick={() => navigate(`/groups/${h.uuid}`)}
                className="cursor-pointer border-b-[0.5px] border-[var(--rule)] transition-colors duration-100 hover:bg-[var(--bg-subtle)]"
              >
                {cols.map((col) => (
                  <td key={col.id} className="cv-data-table-body px-4 py-3 text-[var(--ink)]">
                    {col.cell?.(h)}
                  </td>
                ))}
              </tr>
              {expanded.has(h.uuid) && (
                <GroupPropertyRows groupUuid={h.uuid} visibleColumns={cols} />
              )}
            </Fragment>
          )}
          emptyStateNode={
            <EmptyState
              icon={Building2}
              title={
                groupRows.length === 0
                  ? t('groups.home.emptyTitle', 'No {{groupNounPlural}} yet')
                  : t('groups.home.noMatchTitle', 'No {{groupNounPlural}} match')
              }
              description={
                groupRows.length === 0
                  ? t(
                      'groups.home.emptyBody',
                      'Set up your first {{groupNoun}}, then add or upload its properties.',
                    )
                  : t('groups.home.noMatchBody', 'Try another name, address or contact.')
              }
              action={
                groupRows.length === 0 && canEdit
                  ? {
                      label: t('groups.home.addGroup', 'Add {{groupNoun}}'),
                      onClick: () => navigate('/groups/new'),
                    }
                  : undefined
              }
            />
          }
        />

        <Dialog open={findOpen} onOpenChange={setFindOpen}>
          <DialogContent className="sm:max-w-[560px]">
            <DialogHeader>
              <DialogTitle className="text-[16px] font-normal text-[var(--ink)]">
                {t('groups.home.findTitle', 'Find a property')}
              </DialogTitle>
              <DialogDescription className="text-[13px] text-[var(--ink-2)]">
                {t(
                  'groups.home.findBody',
                  'Search by address, ZIP, owner or registration number. The property opens with its registrations, claims and transfers.',
                )}
              </DialogDescription>
            </DialogHeader>
            <HostSearchInput
              hideHeading
              onSelect={(result) => {
                setFindOpen(false)
                navigate(`/properties/${result.uuid}`)
              }}
            />
          </DialogContent>
        </Dialog>

        <EditFormDialog
          open={pickFor !== null}
          onOpenChange={(o) => !o && setPickFor(null)}
          title={
            pickFor === 'claims'
              ? t('groups.home.taskClaimsTitle', "Upload a {{groupNoun}}'s claims")
              : t('groups.home.uploadTitle', "Upload a {{groupNoun}}'s properties")
          }
          fields={pickGroupFields}
          values={{ group: groupRows[0]?.uuid ?? '' }}
          submitLabel={t('groups.home.pickContinue', 'Continue')}
          onSave={async (values) => {
            const chosen = groupRows.find((h) => h.uuid === values.group) ?? null
            if (pickFor === 'claims') setClaimsTo(chosen)
            else setUploadTo(chosen)
            setPickFor(null)
          }}
        />

        {claimsTo && claimUploadTarget && (
          <BulkImportDialog
            open={!!claimsTo}
            onOpenChange={(o) => !o && setClaimsTo(null)}
            target={claimUploadTarget}
            extraParams={{ group: claimsTo.uuid }}
            title={t('groups.detail.uploadClaimsTitle', 'Upload Claims for {{name}}', {
              name: claimsTo.name,
            })}
            onComplete={() => void refetch()}
          />
        )}

        {uploadTo && (
          <BulkImportDialog
            open={!!uploadTo}
            onOpenChange={(o) => !o && setUploadTo(null)}
            target="group_members"
            extraParams={{ group: uploadTo.uuid }}
            title={t('groups.bulk.title', 'Upload Properties to {{name}}', {
              name: uploadTo.name,
            })}
            onComplete={() => void refetch()}
          />
        )}
      </main>
    </AppShell>
  )
}
